import useSeo from "@/hooks/useSeo";

export default function About() {
  useSeo({
    title: "About Dietico & Mesron Healthcare LLP",
    description:
      "Dietico was built by Mesron Healthcare LLP to end paper-first hospital diet workflow chaos in India. Founded by Dr. Indranil Chatterjee, incorporated 2023.",
    path: "/about",
  });
  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="mx-auto max-w-7xl px-6 pt-16 pb-20 md:pt-24 md:pb-24">
        <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-6">
          About Dietico
        </p>
        <h1 className="text-5xl md:text-7xl font-light tracking-tightest leading-[0.95] text-foreground max-w-4xl">
          Built to fix the workflow between ward, dietician and canteen.
        </h1>
        <blockquote className="mt-10 border-l-2 border-accent pl-6 max-w-3xl text-lg md:text-xl text-secondary leading-relaxed">
          &ldquo;Every day across Indian hospitals, a patient&rsquo;s diet
          order travels through paper charts, verbal handovers and manual
          canteen lists — and somewhere along that chain, it gets lost,
          misread, or ignored. Dietico was built to end that.&rdquo;
          <footer className="mt-4 font-mono text-[11px] uppercase tracking-widest text-secondary">
            — Dr. Indranil Chatterjee · Founder, Mesron Healthcare LLP
          </footer>
        </blockquote>
      </section>

      {/* Meta strip */}
      <section className="border-y border-border bg-secondary">
        <div className="mx-auto max-w-7xl px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["Incorporated", "2023 · India"],
            ["Structure", "LLP · Healthcare"],
            ["Platform", "iOS + Android"],
            ["Coverage", "Pan-India"],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
                {k}
              </p>
              <p className="text-foreground text-lg font-medium tracking-tight">
                {v}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Problem */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-4">
            The problem we solve
          </p>
          <h2 className="text-3xl md:text-5xl font-light tracking-tightest text-foreground">
            India&rsquo;s hospital diet workflow is still paper-first.
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-border text-secondary text-base md:text-lg leading-relaxed space-y-6">
          <p>
            Walk into most Indian hospitals today — private or government —
            and the diet order workflow looks the same: a nurse writes a diet
            chart by hand, a ward boy carries it to the dietician, and the
            canteen receives a verbal instruction or a scanned photocopy.
          </p>
          <p>
            Names get confused. Prescriptions get overridden without record.
            Patients with severe food allergies receive the wrong meal.
            Dieticians sign off on orders they haven&rsquo;t fully reviewed.
            And when something goes wrong, there is no audit trail.
          </p>
          <p className="text-foreground">
            This is not a resource problem. It is a systems problem. And it
            is entirely solvable.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              [
                "One source of truth",
                "Every diet order — from nurse entry to dietician sign-off to canteen receipt — lives in one verified, timestamped record.",
              ],
              [
                "Zero verbal handovers",
                "Push notifications replace verbal rounds. Canteens receive validated, ward-wise orders the moment a dietician signs.",
              ],
              [
                "Complete accountability",
                "Every edit is versioned. Every signature is logged. Every change is traceable to the person, the time, and the reason.",
              ],
              [
                "Patient safety first",
                "Allergy flags, therapeutic diets, and RT feed instructions travel with the order — not separately, not verbally.",
              ],
            ].map(([t, b]) => (
              <div
                key={t}
                className="rounded-2xl border border-border bg-secondary p-8 md:p-10"
              >
                <h3 className="text-xl md:text-2xl font-medium tracking-tight text-foreground">
                  {t}
                </h3>
                <p className="mt-3 text-secondary leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we are */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-4">
              Who we are
            </p>
            <h2 className="text-3xl md:text-5xl font-light tracking-tightest text-foreground">
              Mesron Healthcare LLP
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-border text-secondary text-base md:text-lg leading-relaxed space-y-6">
            <p>
              Mesron Healthcare LLP is an Indian Limited Liability Partnership
              incorporated in 2023, operating at the intersection of clinical
              practice and healthcare technology. Our focus is narrow and
              deliberate: digitising the workflows that directly affect
              patient safety and clinical nutrition outcomes in Indian
              hospitals.
            </p>
            <p>
              We are not a generic health-tech company. We do not build
              patient-facing consumer apps or hospital management suites.
              Dietico is our singular product — built for one purpose, built
              well.
            </p>
            <p>
              All rights to the Dietico platform, including the mobile
              application, backend infrastructure, proprietary algorithms and
              associated documentation, are exclusively reserved by Mesron
              Healthcare LLP.
            </p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <div className="rounded-3xl border border-border bg-secondary p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-4">
                Founder
              </p>
              <h3 className="text-2xl md:text-3xl font-medium tracking-tight text-foreground">
                Dr. Indranil Chatterjee
              </h3>
              <p className="mt-2 text-secondary text-sm">
                Founder &amp; Grievance Officer, Mesron Healthcare LLP
              </p>
            </div>
            <div className="lg:col-span-8 text-secondary leading-relaxed space-y-5 text-base md:text-lg">
              <p>
                Dr. Chatterjee founded Mesron Healthcare LLP with a single
                conviction: that the most consequential failures in hospital
                care are not surgical or pharmaceutical — they are logistical.
                A patient with chronic kidney disease receiving a
                high-potassium meal because a paper chart was misread. A
                post-surgical patient given solid food because a verbal order
                was misheard. These are preventable.
              </p>
              <p className="text-foreground">Dietico was built to make them impossible.</p>
              <p>
                Dr. Chatterjee serves as the designated Grievance Officer
                under the DPDP Act 2023 and the IT (Intermediary Guidelines)
                Rules 2021, and is personally accountable for the data
                protection and privacy commitments made by the platform.
              </p>
              <div className="pt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                <a
                  href="mailto:admin@dietico.co.in"
                  className="text-foreground hover:opacity-80"
                  data-testid="about-email"
                >
                  admin@dietico.co.in
                </a>
                <a
                  href="tel:+919002296906"
                  className="text-foreground hover:opacity-80"
                  data-testid="about-phone"
                >
                  +91 9002296906
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-6">
            What we stand for
          </p>
          <h2 className="text-3xl md:text-5xl font-light tracking-tightest text-foreground max-w-3xl mb-16">
            Our principles.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14 max-w-5xl">
            {[
              [
                "Clinical integrity above all",
                "Every feature in Dietico exists to reduce clinical error, not to add features for their own sake. If a workflow change does not improve patient safety or professional accountability, we do not build it.",
              ],
              [
                "Patient data is a trust, not a resource",
                "We do not monetise patient data. We do not share it beyond what is necessary for the platform to function. We store it in India, on Indian infrastructure, under Indian law.",
              ],
              [
                "Accountability at every step",
                "The dietician who signed an order, the nurse who entered the diagnosis, the canteen that received the instruction — every action is logged, versioned, and retrievable.",
              ],
              [
                "Steady, deliberate growth",
                "Mesron Healthcare LLP is not chasing user growth metrics. We are building a platform Indian hospitals can depend on — hospital by hospital, ward by ward.",
              ],
              [
                "Built for Indian healthcare",
                "The food database references FSSAI-recognised categories. Nutritional calculations align with NIN Dietary Guidelines for Indians. The workflow reflects how Indian hospitals actually operate.",
              ],
            ].map(([t, b]) => (
              <div key={t}>
                <h3 className="text-lg md:text-xl font-medium tracking-tight text-foreground">
                  {t}
                </h3>
                <p className="mt-3 text-secondary leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
